<script setup lang="ts">
// ToastHost — the toast render surface (FE-00). Mount ONCE near the app root
// (e.g. in App.vue). Reads the shared useToast() queue and renders each toast in
// a teleported, aria-live region so screen readers announce them; errors use
// role="alert" (assertive), the rest role="status" (polite). Token-driven tones
// mirror Banner. Screens never render this — they call `useToast().push(...)`.
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-vue-next';
import { useToast, type ToastTone } from '@/composables/useToast';

const { toasts, dismiss } = useToast();

const ICONS = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  error: XCircle,
} as const;

const TONES: Record<ToastTone, string> = {
  info: 'border-line [--icon:var(--ink2)]',
  success: 'border-acc/50 [--icon:var(--acc)]',
  warning: 'border-gold/60 [--icon:var(--gold)]',
  error: 'border-brand/60 [--icon:var(--brand)]',
};
</script>

<template>
  <Teleport to="body">
    <div
      class="pointer-events-none fixed inset-x-0 bottom-0 z-[60] flex flex-col items-center gap-2 p-4 md:inset-x-auto md:right-0 md:items-end"
    >
      <TransitionGroup name="dc-toast">
        <div
          v-for="t in toasts"
          :key="t.id"
          :role="t.tone === 'error' ? 'alert' : 'status'"
          :aria-live="t.tone === 'error' ? 'assertive' : 'polite'"
          :class="[
            'pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-field border bg-sf px-4 py-3 shadow-glow',
            TONES[t.tone],
          ]"
        >
          <component
            :is="ICONS[t.tone]"
            class="mt-0.5 size-5 shrink-0 text-[var(--icon)]"
            aria-hidden="true"
          />
          <div class="min-w-0 flex-1">
            <p class="text-sm font-semibold text-ink">{{ t.title }}</p>
            <p v-if="t.message" class="mt-0.5 text-sm text-ink2">{{ t.message }}</p>
          </div>
          <button
            type="button"
            aria-label="Dismiss notification"
            class="shrink-0 rounded-field p-1 text-ink2 transition hover:bg-sf2 hover:text-ink"
            @click="dismiss(t.id)"
          >
            <X class="size-4" aria-hidden="true" />
          </button>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<style scoped>
.dc-toast-enter-active,
.dc-toast-leave-active {
  transition: all 0.22s ease;
}
.dc-toast-enter-from,
.dc-toast-leave-to {
  opacity: 0;
  transform: translateY(0.75rem);
}
</style>
